import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "../components/Layout";
import { DocumentAPI } from "../lib/api";
import { useCenturion } from "../context/CenturionContext";
import { toast } from "sonner";
import {
  Search, FileText, Eye, Trash2, Plus, RefreshCw, Wand2, Lock, Layers,
} from "lucide-react";

const DocumentsListPage = () => {
  const { unlocked } = useCenturion();
  const [docs, setDocs] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(true);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [list, an] = await Promise.all([
        DocumentAPI.list({ search, status }),
        DocumentAPI.analytics(),
      ]);
      setDocs(list);
      setAnalytics(an);
    } catch (e) {
      toast.error("Failed to load documents");
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable react-hooks/exhaustive-deps */ }, [search, status]);

  const handleDelete = async (id) => {
    try {
      await DocumentAPI.remove(id);
      toast.success("Document deleted");
      setConfirmDelete(null);
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const sortedByDate = useMemo(
    () => (Array.isArray(docs) ? [...docs] : []).sort((a, b) => (a.created_at < b.created_at ? 1 : -1)),
    [docs]
  );

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-6 py-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] text-[#d4a54b] mb-2">Overview</div>
            <h1 className="font-display text-[40px] md:text-[52px] font-semibold leading-[1.05] tracking-tight">
              Documents
            </h1>
            <p className="text-white/55 text-[14px] mt-2 max-w-xl">
              Every NDA, offer letter, term sheet, and policy drafted through Document Studio.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={load} className="btn-ghost" disabled={loading}>
              <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refresh
            </button>
            {unlocked && (
              <Link to="/studio/documents" className="btn-primary">
                <Plus size={14} /> NEW DOCUMENT
              </Link>
            )}
          </div>
        </div>

        {analytics && Object.keys(analytics.by_type || {}).length > 0 && (
          <section className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
            <StatCard icon={<Layers size={16} />} label="Total Documents" value={analytics.count} accent />
            {Object.entries(analytics.by_type).slice(0, 3).map(([type, count]) => (
              <StatCard key={type} icon={<FileText size={16} />} label={type.replace(/-/g, " ")} value={count} />
            ))}
          </section>
        )}

        <div className="flex flex-col md:flex-row md:items-center gap-3 mb-6">
          <div className="flex-1 relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by title or counterparty…"
              className="field pl-10"
            />
          </div>
          <div className="flex items-center gap-1 bg-white/4 border border-white/8 rounded-md p-1">
            {[{ key: "", label: "All" }, { key: "draft", label: "Draft" }, { key: "final", label: "Final" }].map((t) => (
              <button
                key={t.key}
                onClick={() => setStatus(t.key)}
                className={`px-3 py-1.5 text-[12px] rounded transition-colors ${status === t.key ? "bg-[#d4a54b] text-black font-semibold" : "text-white/60 hover:text-white"}`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="grid gap-3">
            {[0, 1, 2].map((i) => <div key={i} className="glass h-[80px] animate-pulse opacity-60" />)}
          </div>
        ) : sortedByDate.length === 0 ? (
          <EmptyState unlocked={unlocked} />
        ) : (
          <div className="grid gap-3">
            {sortedByDate.map((doc) => (
              <DocRow key={doc.id} doc={doc} unlocked={unlocked} onAskDelete={() => setConfirmDelete(doc)} />
            ))}
          </div>
        )}
      </div>

      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
          <div className="glass max-w-md w-full mx-4 p-6">
            <h3 className="text-[18px] font-semibold mb-2">Delete document?</h3>
            <p className="text-white/60 text-[13px] leading-relaxed">
              You're about to delete <span className="text-white">{confirmDelete.title}</span>. This cannot be undone.
            </p>
            <div className="flex justify-end gap-2 mt-6">
              <button className="btn-ghost" onClick={() => setConfirmDelete(null)}>Cancel</button>
              <button className="btn-danger" onClick={() => handleDelete(confirmDelete.id)}>
                <Trash2 size={14} /> Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

const StatCard = ({ icon, label, value, accent }) => (
  <div className="glass p-5">
    <div className="flex items-center gap-2 text-white/55 text-[11px] uppercase tracking-[0.12em] capitalize">
      <span className={accent ? "text-[#d4a54b]" : ""}>{icon}</span> {label}
    </div>
    <div className={`mt-3 text-[26px] font-semibold tracking-tight ${accent ? "text-[#e0bc62]" : ""}`}>{value}</div>
  </div>
);

const DocRow = ({ doc, unlocked, onAskDelete }) => {
  const genState = doc.generation?.state;
  return (
    <div className="glass interactive p-5 flex flex-col md:flex-row md:items-center gap-4">
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-2 mb-1.5">
          <span className="font-mono text-[11px] text-[#d4a54b] tracking-wider uppercase">{(doc.doc_type || "document").replace(/-/g, " ")}</span>
          <span className={`status-badge ${doc.status === "final" ? "paid" : "draft"}`}>{doc.status}</span>
          {genState === "pending" && <span className="status-badge sent">drafting…</span>}
          {genState === "fallback" && <span className="status-badge overdue">template</span>}
        </div>
        <h3 className="text-[16px] font-medium leading-snug truncate">{doc.title}</h3>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-[12px] text-white/55">
          {doc.counterparty?.name && <span>For: <span className="text-white/80">{doc.counterparty.name}</span></span>}
          <span>{new Date(doc.created_at).toLocaleDateString()}</span>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Link
          to={`/documents/${doc.id}`}
          className="px-3 py-2 rounded-md bg-white/5 hover:bg-white/10 border border-white/10 text-[12px] flex items-center gap-1.5 transition-colors"
        >
          <Eye size={14} /> View
        </Link>
        {unlocked && (
          <button
            onClick={onAskDelete}
            className="px-2.5 py-2 rounded-md bg-white/5 hover:bg-red-500/10 border border-white/10 hover:border-red-500/40 text-[12px] flex items-center gap-1.5 text-white/70 hover:text-red-400 transition-colors"
            aria-label="Delete document"
          >
            <Trash2 size={14} />
          </button>
        )}
      </div>
    </div>
  );
};

const EmptyState = ({ unlocked }) => (
  <div className="glass p-14 text-center">
    <div className="w-14 h-14 rounded-full bg-[rgba(212,165,75,0.08)] mx-auto flex items-center justify-center text-[#d4a54b] mb-5">
      <Wand2 size={24} />
    </div>
    <h3 className="font-display text-[28px] font-semibold">No documents yet</h3>
    <p className="text-white/60 text-[13px] mt-2 max-w-sm mx-auto">
      Open Document Studio to draft your first NDA, offer letter, or policy.
    </p>
    <div className="flex items-center justify-center gap-3 mt-6">
      <Link to="/studio/documents" className="btn-primary">
        <Wand2 size={14} /> Open Document Studio
      </Link>
    </div>
    {!unlocked && (
      <p className="mt-6 text-[11px] text-white/35 flex items-center justify-center gap-1.5">
        <Lock size={11} /> Issuer mode locked — type the keyword to draft and issue documents.
      </p>
    )}
  </div>
);

export default DocumentsListPage;
